import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import {
  MdOutlineChair,
  MdOutlineSupervisorAccount,
  MdOutlineSchool,
  MdOutlineBusinessCenter,
  MdOutlineVolunteerActivism,
} from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";

const sections = [
  {
    title: "Chairman",
    icon: <MdOutlineChair size={22} />,
    desc: "Our Chairman leads the institution with a visionary mindset, ensuring strategic growth and maintaining academic excellence.",
    image: "https://via.placeholder.com/600x200?text=Chairman",
    color: "#f4c842",
    border: "rgba(244,200,66,0.2)",
  },
  {
    title: "Vice Principal",
    icon: <MdOutlineSupervisorAccount size={22} />,
    desc: "The Vice Principal manages academic schedules, supports students, and maintains discipline across all departments.",
    image: "https://via.placeholder.com/600x200?text=Vice+Principal",
    color: "#8b5cf6",
    border: "rgba(139,92,246,0.2)",
  },
  {
    title: "Faculty",
    icon: <MdOutlineSchool size={22} />,
    desc: "Our faculty comprises experienced educators who specialize in various subjects, nurturing students with dedication.",
    image: "https://via.placeholder.com/600x200?text=Faculty",
    color: "#ec4899",
    border: "rgba(236,72,153,0.2)",
  },
  {
    title: "Management",
    icon: <MdOutlineBusinessCenter size={22} />,
    desc: "Efficient management staff handle day-to-day operations, admissions, finance, and infrastructure needs.",
    image: "https://via.placeholder.com/600x200?text=Management",
    color: "#6366f1",
    border: "rgba(99,102,241,0.2)",
  },
  {
    title: "Helpers & Staff",
    icon: <MdOutlineVolunteerActivism size={22} />,
    desc: "Our support staff keeps the school functioning smoothly — from cleaning, maintenance to student assistance.",
    image: "https://via.placeholder.com/600x200?text=Helpers+%26+Staff",
    color: "#06b6d4",
    border: "rgba(6,182,212,0.2)",
  },
];

const FacultyPage = () => {
  const [open, setOpen] = useState(null);

  return (
    <section className="relative min-h-screen pt-36 md:pt-40 pb-20 px-6 md:px-16 md:pt-[96px] overflow-hidden">
      <div
        className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-4xl mx-auto relative z-10">
        <Link
          to="/information"
          className="inline-block text-sm font-bold tracking-wide mb-8"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Information
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Our School Faculty
        </h1>
        <div className="cosmic-divider mb-10" />

        <div className="space-y-4">
          {sections.map((s, i) => (
            <div
              key={i}
              className="glass-card overflow-hidden transition-all duration-300"
              style={{
                borderColor: open === i ? s.border : "rgba(255,255,255,0.06)",
              }}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5"
                onClick={() => setOpen(open === i ? null : i)}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${s.color}18`,
                      border: `1px solid ${s.border}`,
                      color: s.color,
                    }}
                  >
                    {s.icon}
                  </div>
                  <span
                    className="font-semibold text-left"
                    style={{
                      fontFamily: "Cinzel, serif",
                      color: "#f0f5ff",
                      fontSize: "1rem",
                    }}
                  >
                    {s.title}
                  </span>
                </div>
                <span style={{ color: s.color }}>
                  {open === i ? (
                    <IoIosArrowUp size={18} />
                  ) : (
                    <IoIosArrowDown size={18} />
                  )}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    key="body"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div
                      className="px-6 pb-6 pt-2"
                      style={{ borderTop: `1px solid ${s.border}` }}
                    >
                      <p
                        className="mb-5 mt-4"
                        style={{
                          fontFamily: "Nunito, sans-serif",
                          color: "#8899b8",
                          fontSize: "0.9rem",
                          lineHeight: "1.8",
                        }}
                      >
                        {s.desc}
                      </p>
                      <img
                        src={s.image}
                        alt={s.title}
                        className="rounded-xl w-full max-w-xl"
                        style={{ border: `1px solid ${s.border}` }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FacultyPage;
